/**
 * VoiceAgentCompletion - Component shown when AI Voice Agent setup flow completes
 *
 * Horizontal banner style matching campaign-success component.
 * Uses blue color scheme to indicate "in progress/configuring" state.
 */

import React, { FC } from 'react';
import {
  Box,
  Flex,
  Text,
} from '@chakra-ui/react';

interface VoiceAgentCompletionProps {
  businessName?: string;
  industry?: string;
  phone?: string;
}

const VoiceAgentCompletion: FC<VoiceAgentCompletionProps> = ({
  businessName = '',
}) => {
  const parsedName = businessName ? String(businessName) : 'Your business';

  return (
    <Flex
      w="100%"
      maxW="420px"
      bg="blue.50"
      borderRadius="lg"
      border="1px solid"
      borderColor="blue.200"
      p={3}
      my={2}
      direction={{ base: 'column', sm: 'row' }}
      align={{ base: 'flex-start', sm: 'center' }}
      gap={3}
    >
      <Flex align="center" gap={3} minW={0} flex={1}>
        {/* In Progress Icon with pulse animation */}
        <Box
          w="36px"
          h="36px"
          bg="blue.500"
          borderRadius="full"
          display="flex"
          alignItems="center"
          justifyContent="center"
          flexShrink={0}
          position="relative"
        >
          {/* Pulsing ring */}
          <Box
            position="absolute"
            w="100%"
            h="100%"
            borderRadius="full"
            bg="blue.400"
            opacity={0.5}
            sx={{
              animation: 'pulse-ring 2s ease-out infinite',
              '@keyframes pulse-ring': {
                '0%': { transform: 'scale(1)', opacity: 0.5 },
                '100%': { transform: 'scale(1.5)', opacity: 0 },
              },
            }}
          />
          {/* Phone/Voice icon */}
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M14.05 2a9 9 0 018 7.94M14.05 6A5 5 0 0118 10"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </Box>

        {/* Text Content */}
        <Box flex={1} minW={0}>
          <Text fontSize="sm" fontWeight="semibold" color="blue.800" lineHeight="short">
            AI Voice Agent Configuring
          </Text>
          <Text fontSize="xs" color="blue.700" noOfLines={1} title={parsedName}>
            {parsedName}
          </Text>
        </Box>
      </Flex>

      {/* Status indicator */}
      <Flex
        align="center"
        gap={1.5}
        bg="blue.100"
        px={2.5}
        py={1}
        borderRadius="full"
        flexShrink={0}
      >
        <Box
          w="6px"
          h="6px"
          borderRadius="full"
          bg="blue.500"
          sx={{
            animation: 'pulse 1.5s ease-in-out infinite',
            '@keyframes pulse': {
              '0%, 100%': { opacity: 1 },
              '50%': { opacity: 0.4 },
            },
          }}
        />
        <Text fontSize="xs" fontWeight="medium" color="blue.700">
          In Progress
        </Text>
      </Flex>
    </Flex>
  );
};

export default VoiceAgentCompletion;
